// Carte d'attention (modèle d'IA par patchs) : lecture du CSV des patchs et calage de leurs coordonnées sur le HES de
// l'échantillon. Fonctions pures, sans API du navigateur (testables sous Node).
//
// Le CSV donne, par patch, le coin (x, y) en px d'une lame scannée ailleurs (autre scanner, autre résolution, coupe
// tournée…) et son attention. Le calage cherche la transformation affine patchs -> grille commune en deux temps :
// 1. forme : le nuage de patchs (un échantillon aléatoire des patchs du tissu) doit couvrir le tissu du HES, sans en
//    sortir. Distances de chanfrein dans les deux sens (centres des patchs -> tissu, tissu -> patch le plus proche),
//    calculées sur des cartes de distance faites une fois. Similitudes (rotation tous les 15°, échelles autour du
//    rapport des étendues, miroir) puis Nelder-Mead, en similitude puis en affine ;
// 2. contenu : l'attention d'un patch dépend de ce qu'il montre. Sous la bonne transformation, la couleur moyenne
//    du HES sous chaque patch (R, G, B, saturation, écart-type, et leurs carrés) explique au mieux le rang de son
//    attention (R² d'une régression linéaire). Nelder-Mead affine sur ce R², depuis les meilleures solutions de
//    l'étape 1 (et l'identité : coordonnées déjà dans la grille du HES).
// Mis au point sur 215149H (3 000 patchs de 512 px, environ 14 % du tissu, coordonnées d'un autre scan du HES tourné
// de 12,8°) : la forme seule laisse jusqu'à ~1 000 px d'incertitude (cisaillement mal contraint), le contenu ramène à
// quelques dizaines de px (R² 0,44).

const tick = () => new Promise(resolve => setTimeout(resolve, 0));

// ------------------------------------------------------------ CSV

const HEADERS = {
  x: ['x', 'x_px', 'coord_x', 'xmin', 'left', 'col'],
  y: ['y', 'y_px', 'coord_y', 'ymin', 'top', 'row'],
  v: ['attention', 'attn', 'attention_score', 'contribution', 'contrib', 'shap', 'importance', 'score', 'logit', 'weight',
    'value'],
};
const NOT_VALUES = ['patch', 'patch_id', 'id', 'index', 'idx', 'level', 'width', 'height', 'size', 'w', 'h'];

// Lit le CSV des patchs (colonnes x, y et une valeur par patch : attention, contribution… ; séparateur virgule,
// point-virgule ou tabulation). Valeur : colonne au nom connu, sinon la dernière colonne qui n'est ni x, ni y, ni un
// identifiant. Taille des patchs : écart le plus fréquent entre deux colonnes de patchs voisines (grille régulière).
// valueName : nom de la colonne (légendes).
export function parsePatches(text) {
  const lines = text.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) throw new Error('The file has no patches.');
  const sep = [',', ';', '\t'].sort((a, b) => lines[0].split(b).length - lines[0].split(a).length)[0];
  const names = lines[0].split(sep).map(h => h.trim().replace(/^"|"$/g, ''));
  const head = names.map(h => h.toLowerCase());
  const col = key => head.findIndex(h => HEADERS[key].includes(h));
  const [ix, iy] = ['x', 'y'].map(col);
  let iv = col('v');
  if (iv < 0) iv = head.map((h, i) => i).filter(i => i !== ix && i !== iy && !NOT_VALUES.includes(head[i])).pop() ?? -1;
  if (ix < 0 || iy < 0 || iv < 0) {
    throw new Error(`Columns "x", "y" and a value (attention, contribution…) are required (found: ${names.join(', ')}).`);
  }
  const x = [];
  const y = [];
  const v = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = lines[i].split(sep);
    const [a, b, c] = [ix, iy, iv].map(k => Number(cells[k]));
    if (Number.isFinite(a) && Number.isFinite(b) && Number.isFinite(c)) { x.push(a); y.push(b); v.push(c); }
  }
  if (x.length < 10) throw new Error('Fewer than 10 patches could be read.');
  const xs = [...new Set(x.map(Math.round))].sort((a, b) => a - b);
  const gaps = {};
  for (let i = 1; i < xs.length; i += 1) { const d = xs[i] - xs[i - 1]; if (d > 0) gaps[d] = (gaps[d] || 0) + 1; }
  const patchSize = Number(Object.entries(gaps).sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0]) || 256;
  return {
    x: Float64Array.from(x), y: Float64Array.from(y), v: Float64Array.from(v), count: x.length, patchSize, valueName: names[iv],
  };
}

// Patchs enregistrés dans Firestore : x, y, attention entrelacés en Float32, en base64 (12 octets par patch).
export function encodePatches({ x, y, v, count }) {
  const values = new Float32Array(3 * count);
  for (let i = 0; i < count; i += 1) { values[3 * i] = x[i]; values[3 * i + 1] = y[i]; values[3 * i + 2] = v[i]; }
  const bytes = new Uint8Array(values.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export function decodePatches(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const values = new Float32Array(bytes.buffer);
  const count = values.length / 3;
  const x = new Float64Array(count);
  const y = new Float64Array(count);
  const v = new Float64Array(count);
  for (let i = 0; i < count; i += 1) { x[i] = values[3 * i]; y[i] = values[3 * i + 1]; v[i] = values[3 * i + 2]; }
  return { x, y, v, count };
}

// Rang de chaque attention ramené à [0, 1] (centile) : les attentions d'un softmax sont très resserrées, le rang les
// étale sur toute la palette.
export function attentionRanks(v) {
  const order = Array.from(v.keys()).sort((a, b) => v[a] - v[b]);
  const rank = new Float32Array(v.length);
  order.forEach((i, k) => { rank[i] = k / Math.max(1, v.length - 1); });
  return rank;
}

// ------------------------------------------------------------ tissu du HES

// Tissu sur un niveau réduit du HES (rgb : comps composantes par pixel) : coloré (max − min ≥ 20) ou sombre (gris
// < 180), le fond d'une lame étant gris clair et neutre ; ouverture 3 × 3 contre les pixels isolés.
export function tissueMask({ rgb, comps, width: w, height: h }) {
  const raw = new Uint8Array(w * h);
  for (let i = 0, j = 0; i < raw.length; i += 1, j += comps) {
    const r = rgb[j]; const g = rgb[j + 1]; const b = rgb[j + 2];
    const hi = Math.max(r, g, b);
    const lo = Math.min(r, g, b);
    raw[i] = hi - lo >= 20 || (r + g + b) / 3 < 180 ? 1 : 0;
  }
  return morph(morph(raw, w, h, Math.min), w, h, Math.max);
}

// Érosion (min) ou dilatation (max) 3 × 3, séparable.
function morph(src, w, h, op) {
  const tmp = new Uint8Array(src.length);
  const out = new Uint8Array(src.length);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      tmp[i] = op(src[i], src[x > 0 ? i - 1 : i], src[x < w - 1 ? i + 1 : i]);
    }
  }
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = y * w + x;
      out[i] = op(tmp[i], tmp[y > 0 ? i - w : i], tmp[y < h - 1 ? i + w : i]);
    }
  }
  return out;
}

// Distance euclidienne exacte au pixel « 1 » le plus proche (Felzenszwalb et Huttenlocher), en pixels.
export function distanceTransform(mask, w, h) {
  const INF = 1e20;
  const f = new Float64Array(Math.max(w, h));
  const d = new Float64Array(Math.max(w, h));
  const vv = new Int32Array(Math.max(w, h));
  const z = new Float64Array(Math.max(w, h) + 1);
  const pass = n => {
    let k = 0;
    vv[0] = 0; z[0] = -INF; z[1] = INF;
    for (let q = 1; q < n; q += 1) {
      let s = ((f[q] + q * q) - (f[vv[k]] + vv[k] * vv[k])) / (2 * q - 2 * vv[k]);
      while (s <= z[k]) { k -= 1; s = ((f[q] + q * q) - (f[vv[k]] + vv[k] * vv[k])) / (2 * q - 2 * vv[k]); }
      k += 1; vv[k] = q; z[k] = s; z[k + 1] = INF;
    }
    k = 0;
    for (let q = 0; q < n; q += 1) {
      while (z[k + 1] < q) k += 1;
      d[q] = (q - vv[k]) ** 2 + f[vv[k]];
    }
  };
  const out = new Float64Array(w * h);
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < h; y += 1) f[y] = mask[y * w + x] ? 0 : INF;
    pass(h);
    for (let y = 0; y < h; y += 1) out[y * w + x] = d[y];
  }
  const res = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) f[x] = out[y * w + x];
    pass(w);
    for (let x = 0; x < w; x += 1) res[y * w + x] = Math.sqrt(d[x]);
  }
  return res;
}

// ------------------------------------------------------------ optimisation

// Nelder-Mead (minimisation), rend la main au navigateur de temps en temps.
async function nelderMead(fun, x0, steps, { iterations = 1000, tol = 1e-7 } = {}) {
  const n = x0.length;
  let pts = [Float64Array.from(x0)];
  for (let i = 0; i < n; i += 1) { const p = Float64Array.from(x0); p[i] += steps[i]; pts.push(p); }
  let vals = pts.map(fun);
  const comb = (a, b, t) => a.map((ai, i) => ai + t * (b[i] - ai));
  for (let it = 0; it < iterations; it += 1) {
    if (it % 40 === 39) await tick();
    const order = vals.map((val, i) => i).sort((a, b) => vals[a] - vals[b]);
    pts = order.map(i => pts[i]);
    vals = order.map(i => vals[i]);
    if (Math.abs(vals[n] - vals[0]) < tol) break;
    const c = new Float64Array(n);
    for (let i = 0; i < n; i += 1) for (let k = 0; k < n; k += 1) c[k] += pts[i][k] / n;
    const xr = comb(c, pts[n], -1);
    const fr = fun(xr);
    if (fr < vals[0]) {
      const xe = comb(c, pts[n], -2);
      const fe = fun(xe);
      [pts[n], vals[n]] = fe < fr ? [xe, fe] : [xr, fr];
    } else if (fr < vals[n - 1]) {
      [pts[n], vals[n]] = [xr, fr];
    } else {
      const xc = comb(c, pts[n], 0.5);
      const fc = fun(xc);
      if (fc < vals[n]) {
        [pts[n], vals[n]] = [xc, fc];
      } else {
        for (let i = 1; i <= n; i += 1) { pts[i] = comb(pts[0], pts[i], 0.5); vals[i] = fun(pts[i]); }
      }
    }
  }
  const best = vals.indexOf(Math.min(...vals));
  return { x: pts[best], value: vals[best] };
}

// Affine [a, b, c, d, e, f] : X = a x + b y + c, Y = d x + e y + f.
const det = A => A[0] * A[4] - A[1] * A[3];
const invert = A => {
  const k = det(A);
  return [A[4] / k, -A[1] / k, (A[1] * A[5] - A[4] * A[2]) / k, -A[3] / k, A[0] / k, (A[3] * A[2] - A[0] * A[5]) / k];
};
const similarity = ([logS, theta, tx, ty], mirror) => {
  const s = Math.exp(logS);
  const m = mirror ? -1 : 1;
  return [s * Math.cos(theta), -m * s * Math.sin(theta), tx, s * Math.sin(theta), m * s * Math.cos(theta), ty];
};

// Échelle d'un nuage de points : racine quatrième du déterminant de sa covariance.
function spread(xs, ys) {
  const n = xs.length;
  let mx = 0; let my = 0;
  for (let i = 0; i < n; i += 1) { mx += xs[i] / n; my += ys[i] / n; }
  let sxx = 0; let syy = 0; let sxy = 0;
  for (let i = 0; i < n; i += 1) {
    const dx = xs[i] - mx; const dy = ys[i] - my;
    sxx += dx * dx / n; syy += dy * dy / n; sxy += dx * dy / n;
  }
  return { mx, my, scale: Math.max(1e-9, sxx * syy - sxy * sxy) ** 0.25 };
}

// Résolution de (XᵀX + λI) β = Xᵀy par Cholesky (matrice symétrique définie positive, n petit).
function solveSpd(M, b, n) {
  const L = new Float64Array(n * n);
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j <= i; j += 1) {
      let s = M[i * n + j];
      for (let k = 0; k < j; k += 1) s -= L[i * n + k] * L[j * n + k];
      L[i * n + j] = i === j ? Math.sqrt(Math.max(s, 1e-12)) : s / L[j * n + j];
    }
  }
  const z = new Float64Array(n);
  for (let i = 0; i < n; i += 1) { let s = b[i]; for (let k = 0; k < i; k += 1) s -= L[i * n + k] * z[k]; z[i] = s / L[i * n + i]; }
  const x = new Float64Array(n);
  for (let i = n - 1; i >= 0; i -= 1) { let s = z[i]; for (let k = i + 1; k < n; k += 1) s -= L[k * n + i] * x[k]; x[i] = s / L[i * n + i]; }
  return x;
}

// ------------------------------------------------------------ calage

// Cale les patchs sur le HES. hes = { rgb, comps, width, height, sx, sy } : un niveau réduit du HES (sx, sy : px de
// la grille commune par px de ce niveau). Renvoie { transform: [a, b, c, d, e, f] (coin d'un patch en px du CSV ->
// px de la grille commune), r2 (contenu ; null si la forme seule a servi), method }.
export async function placePatches(patches, hes, { onProgress = () => {} } = {}) {
  const { x, y, count, patchSize: step } = patches;
  const { width: w, height: h, sx, sy } = hes;
  const mask = tissueMask(hes);
  // Centres des patchs (coordonnées du CSV) ; transformation des coins en fin de calcul.
  const px = Float64Array.from(x, v => v + step / 2);
  const py = Float64Array.from(y, v => v + step / 2);

  // Étape 1 : forme. Distance au tissu (px de la grille), points du tissu tirés au hasard.
  onProgress('Matching the patches to the tissue outline…');
  await tick();
  const dTissue = distanceTransform(mask, w, h);
  const tissueIdx = [];
  for (let i = 0; i < mask.length; i += 1) if (mask[i]) tissueIdx.push(i);
  if (tissueIdx.length < 100) throw new Error('No tissue found on the HES.');
  const nT = Math.min(20000, tissueIdx.length);
  const tX = new Float64Array(nT);
  const tY = new Float64Array(nT);
  let seed = 12345;
  const rand = () => { seed = (seed * 1103515245 + 12345) % 2147483648; return seed / 2147483648; };
  for (let k = 0; k < nT; k += 1) {
    const i = tissueIdx[Math.floor(rand() * tissueIdx.length)];
    tX[k] = ((i % w) + 0.5) * sx; tY[k] = (Math.floor(i / w) + 0.5) * sy;
  }
  // Distance au patch le plus proche, sur la grille des patchs (unités du CSV), avec une marge.
  let x0 = Infinity; let y0 = Infinity;
  for (let i = 0; i < count; i += 1) { x0 = Math.min(x0, px[i]); y0 = Math.min(y0, py[i]); }
  const PAD = 40;
  const gx = Float64Array.from(px, v => Math.round((v - x0) / step) + PAD);
  const gy = Float64Array.from(py, v => Math.round((v - y0) / step) + PAD);
  const gw = Math.max(...gx) + PAD + 1;
  const gh = Math.max(...gy) + PAD + 1;
  const occ = new Uint8Array(gw * gh);
  for (let i = 0; i < count; i += 1) occ[gy[i] * gw + gx[i]] = 1;
  const dPatch = distanceTransform(occ, gw, gh);
  const CAP = 1500;  // px de la grille : un patch très loin ne compte pas plus qu'un patch loin
  const shapeCost = A => {
    let t1 = 0;
    for (let i = 0; i < count; i += 1) {
      const u = Math.floor((A[0] * px[i] + A[1] * py[i] + A[2]) / sx);
      const v = Math.floor((A[3] * px[i] + A[4] * py[i] + A[5]) / sy);
      t1 += u >= 0 && v >= 0 && u < w && v < h ? Math.min(dTissue[v * w + u] * sx, CAP) : CAP;
    }
    const B = invert(A);
    const s = Math.sqrt(Math.abs(det(A)));
    let t2 = 0;
    for (let k = 0; k < nT; k += 1) {
      const u = Math.floor((B[0] * tX[k] + B[1] * tY[k] + B[2] - x0) / step + PAD + 0.5);
      const v = Math.floor((B[3] * tX[k] + B[4] * tY[k] + B[5] - y0) / step + PAD + 0.5);
      t2 += u >= 0 && v >= 0 && u < gw && v < gh ? Math.min(dPatch[v * gw + u] * step * s, CAP) : CAP;
    }
    return t1 / count + t2 / nT;
  };
  // Départs : centres superposés, rotations tous les 15°, échelles autour du rapport des étendues, miroir ou non.
  const P = spread(px, py);
  const T = spread(tX, tY);
  const starts = [];
  for (const mirror of [false, true]) {
    for (let deg = -180; deg < 180; deg += 15) {
      for (let k = -4; k <= 4; k += 1) {
        const q = [Math.log((T.scale / P.scale) * 2 ** (k / 4)), (deg * Math.PI) / 180, 0, 0];
        const A = similarity(q, mirror);
        q[2] = T.mx - (A[0] * P.mx + A[1] * P.my);
        q[3] = T.my - (A[3] * P.mx + A[4] * P.my);
        starts.push({ q, mirror, cost: shapeCost(similarity(q, mirror)) });
      }
    }
  }
  starts.sort((a, b) => a.cost - b.cost);
  const shaped = [];
  for (const [k, start] of starts.slice(0, 6).entries()) {
    onProgress(`Matching the patches to the tissue outline… ${Math.round((100 * k) / 6)}%`);
    const sim = await nelderMead(q => shapeCost(similarity(q, start.mirror)), start.q, [0.05, 0.05, 500, 500], { iterations: 300 });
    const A0 = similarity(sim.x, start.mirror);
    const s = Math.sqrt(Math.abs(det(A0)));
    const aff = await nelderMead(shapeCost, A0, [0.01 * s, 0.01 * s, 300, 0.01 * s, 0.01 * s, 300], { iterations: 1500 });
    shaped.push({ A: Array.from(aff.x), cost: aff.value });
  }
  shaped.sort((a, b) => a.cost - b.cost);

  // Étape 2 : contenu. Moyennes de la couleur sous chaque patch par images intégrales (Float32, valeurs centrées :
  // assez précis, deux fois moins de mémoire), coins fractionnaires par interpolation bilinéaire.
  onProgress('Refining with the HES under each patch…');
  await tick();
  const features = 5;  // R, G, B, saturation (max − min), gris²
  const W1 = w + 1;
  const integrals = Array.from({ length: features }, () => new Float32Array(W1 * (h + 1)));
  const means = new Float64Array(features);
  const pixel = new Float64Array(features);
  const valueAt = (i, out) => {
    const j = i * hes.comps;
    const r = hes.rgb[j]; const g = hes.rgb[j + 1]; const b = hes.rgb[j + 2];
    const gray = (r + g + b) / 3;
    out[0] = r; out[1] = g; out[2] = b; out[3] = Math.max(r, g, b) - Math.min(r, g, b); out[4] = gray * gray;
  };
  for (let i = 0; i < w * h; i += 1) { valueAt(i, pixel); for (let f = 0; f < features; f += 1) means[f] += pixel[f] / (w * h); }
  for (let yy = 0; yy < h; yy += 1) {
    const row = new Float64Array(features);
    for (let xx = 0; xx < w; xx += 1) {
      valueAt(yy * w + xx, pixel);
      const o = (yy + 1) * W1 + xx + 1;
      for (let f = 0; f < features; f += 1) {
        row[f] += pixel[f] - means[f];
        integrals[f][o] = integrals[f][o - W1] + row[f];
      }
    }
  }
  const bil = (I, u, v) => {
    u = Math.min(Math.max(u, 0), w - 1e-6); v = Math.min(Math.max(v, 0), h - 1e-6);
    const i = Math.floor(u); const j = Math.floor(v); const a = u - i; const b = v - j;
    const o = j * W1 + i;
    return I[o] * (1 - a) * (1 - b) + I[o + 1] * a * (1 - b) + I[o + W1] * (1 - a) * b + I[o + W1 + 1] * a * b;
  };
  const rank = attentionRanks(patches.v);
  let rankMean = 0;
  for (let i = 0; i < count; i += 1) rankMean += rank[i] / count;
  let sst = 0;
  for (let i = 0; i < count; i += 1) sst += (rank[i] - rankMean) ** 2;
  const nf = 2 * features;  // moyennes et leurs carrés (l'écart-type remplace gris²)
  const X = new Float64Array(count * nf);
  const r2 = A => {
    const s = Math.sqrt(Math.abs(det(A)));
    const hx = (0.5 * step * s) / sx;
    const hy = (0.5 * step * s) / sy;
    for (let i = 0; i < count; i += 1) {
      const u = (A[0] * px[i] + A[1] * py[i] + A[2]) / sx;
      const v = (A[3] * px[i] + A[4] * py[i] + A[5]) / sy;
      const u0 = u - hx; const u1 = u + hx; const v0 = v - hy; const v1 = v + hy;
      const area = Math.max((Math.min(Math.max(u1, 0), w) - Math.min(Math.max(u0, 0), w))
        * (Math.min(Math.max(v1, 0), h) - Math.min(Math.max(v0, 0), h)), 1e-3);
      for (let f = 0; f < features; f += 1) {
        const I = integrals[f];
        pixel[f] = (bil(I, u1, v1) - bil(I, u1, v0) - bil(I, u0, v1) + bil(I, u0, v0)) / area + means[f];
      }
      pixel[4] = Math.sqrt(Math.max(pixel[4] - ((pixel[0] + pixel[1] + pixel[2]) / 3) ** 2, 0));
      for (let f = 0; f < features; f += 1) { X[i * nf + f] = pixel[f]; X[i * nf + features + f] = pixel[f] * pixel[f]; }
    }
    // Colonnes centrées réduites, moindres carrés (petite régularisation), R².
    const mu = new Float64Array(nf);
    const sd = new Float64Array(nf);
    for (let i = 0; i < count; i += 1) for (let f = 0; f < nf; f += 1) mu[f] += X[i * nf + f] / count;
    for (let i = 0; i < count; i += 1) for (let f = 0; f < nf; f += 1) sd[f] += (X[i * nf + f] - mu[f]) ** 2 / count;
    for (let f = 0; f < nf; f += 1) sd[f] = Math.sqrt(sd[f]) || 1;
    const M = new Float64Array(nf * nf);
    const b = new Float64Array(nf);
    const row = new Float64Array(nf);
    for (let i = 0; i < count; i += 1) {
      for (let f = 0; f < nf; f += 1) row[f] = (X[i * nf + f] - mu[f]) / sd[f];
      const t = rank[i] - rankMean;
      for (let f = 0; f < nf; f += 1) {
        b[f] += row[f] * t;
        for (let g = 0; g <= f; g += 1) M[f * nf + g] += row[f] * row[g];
      }
    }
    for (let f = 0; f < nf; f += 1) { M[f * nf + f] += 1e-6 * count; for (let g = 0; g < f; g += 1) M[g * nf + f] = M[f * nf + g]; }
    const beta = solveSpd(M, b, nf);
    let explained = 0;
    for (let f = 0; f < nf; f += 1) explained += beta[f] * b[f];  // SSR = βᵀXᵀy pour les moindres carrés
    return explained / sst;
  };
  const IDENTITY = [1, 0, 0, 0, 1, 0];  // coordonnées déjà dans la grille du HES
  const candidates = [...shaped.slice(0, 2).map(c => c.A), IDENTITY];
  const identityR2 = r2(IDENTITY);
  let best = null;
  for (const [k, A0] of candidates.entries()) {
    onProgress(`Refining with the HES under each patch… ${Math.round((50 * k) / candidates.length)}%`);
    const s = Math.sqrt(Math.abs(det(A0)));
    let start = A0;
    let value = -r2(A0);
    for (let round = 0; round < 2; round += 1) {  // une relance : Nelder-Mead s'arrête parfois trop tôt
      const res = await nelderMead(A => -r2(A), start, [0.01 * s, 0.01 * s, 150, 0.01 * s, 0.01 * s, 150], { iterations: 1200 });
      if (res.value > value - 1e-5) break;
      start = Array.from(res.x); value = res.value;
    }
    if (!best || value < best.value) best = { A: start, value };
  }
  // Contenu sans rapport avec l'attention (R² négligeable) : la forme seule. L'optimum du R² est un plateau d'un
  // demi-patch environ (215149H : solutions équivalentes à 100–150 px les unes des autres, Nelder-Mead s'arrêtant
  // n'importe où dessus) : l'identité, aussi bonne à cette précision près, est gardée (exacte quand le CSV vient du HES
  // envoyé) ; sinon, moyenne des solutions du plateau trouvées depuis des départs décalés (± 1/2 patch, ± 2 %).
  if (-best.value < 0.02) return { transform: shaped[0].A, r2: null, method: 'shape' };
  if (identityR2 >= -best.value - 0.01) return { transform: IDENTITY, r2: identityR2, method: 'identity' };
  const s = Math.sqrt(Math.abs(det(best.A)));
  const half = (step * s) / 2;
  const plateau = [best];
  const JITTER = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];  // x, y, échelle
  for (const [k, [jx, jy, js]] of JITTER.entries()) {
    onProgress(`Refining with the HES under each patch… ${Math.round(50 + (50 * k) / JITTER.length)}%`);
    const start = best.A.slice();
    const g = 1 + 0.02 * js;
    [0, 1, 3, 4].forEach(i => { start[i] *= g; });
    start[2] += jx * half + (1 - g) * (best.A[0] * P.mx + best.A[1] * P.my);
    start[5] += jy * half + (1 - g) * (best.A[3] * P.mx + best.A[4] * P.my);
    const res = await nelderMead(A => -r2(A), start, [0.01 * s, 0.01 * s, 150, 0.01 * s, 0.01 * s, 150], { iterations: 1200 });
    plateau.push({ A: Array.from(res.x), value: res.value });
  }
  const top = Math.min(...plateau.map(p => p.value));
  const kept = plateau.filter(p => p.value <= top + 0.005);
  const mean = Array.from({ length: 6 }, (_, i) => kept.reduce((acc, p) => acc + p.A[i], 0) / kept.length);
  return { transform: mean, r2: r2(mean), method: 'content', solutions: kept.length };
}

// Coins d'un patch dans la grille commune (pour l'affichage) : transformation appliquée au coin (x, y).
export const applyTransform = (A, x, y) => [A[0] * x + A[1] * y + A[2], A[3] * x + A[4] * y + A[5]];
export const transformScale = A => Math.sqrt(Math.abs(det(A)));
export { invert as invertTransform };
